'use client'

import { useMutation } from '@tanstack/react-query'
import Image from 'next/image'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { useTranslations } from 'next-intl'
import { useEffect, useMemo, useState } from 'react'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { useForm } from 'react-hook-form'
import { toast } from 'react-toastify'

import axios from '@/_lib/axiosInstance'
import { pushSimpleEvent } from '@/_utils/analytics/datalayer'

export enum NewsletterColor {
	CYCLAMEN = 'cyclamen',
	PURPLE = 'purple',
	VIOLET = 'violet',
	TURQUOISE = 'turquoise'
}

export interface NewsletterData {
	colorVariant?: NewsletterColor
}

interface FormValues {
	email: string
	agreedToPersonalDataProcessingForMarketingPurposes: boolean
	recaptchaToken?: string | null
}

export interface BEditorJsNewsletterProps {
	data: NewsletterData
}

// Color configurations for each variant
const COLOR_CONFIGS = {
	[NewsletterColor.CYCLAMEN]: {
		color: 'bg-[#EA3476]',
		buttonClass: 'arrow-cyclamen',
		illustration: `${process.env.NEXT_PUBLIC_CDN_URL}/assets/images/illustrations/small-wave/small-medante-wave-cyclamen-02.svg`
	},
	[NewsletterColor.PURPLE]: {
		color: 'bg-[#B138A9]',
		buttonClass: 'arrow-purple',
		illustration: `${process.env.NEXT_PUBLIC_CDN_URL}/assets/images/illustrations/small-wave/small-medante-wave-purple-02.svg`
	},
	[NewsletterColor.VIOLET]: {
		color: 'bg-[#7558C3]',
		buttonClass: 'arrow-violet',
		illustration: `${process.env.NEXT_PUBLIC_CDN_URL}/assets/images/illustrations/small-wave/small-medante-wave-purple-04.svg`
	},
	[NewsletterColor.TURQUOISE]: {
		color: 'bg-[#5CCABD]',
		buttonClass: 'arrow-turquoise',
		illustration: `${process.env.NEXT_PUBLIC_CDN_URL}/assets/images/illustrations/small-wave/small-medante-wave-tirquoise-02.svg`
	}
}

export const BEditorJsNewsletter: React.FC<BEditorJsNewsletterProps> = ({ data }) => {
	const pathname = usePathname()

	const [mounted, setMounted] = useState(false)
	const { executeRecaptcha } = useGoogleReCaptcha()
	const t = useTranslations('editorjs.newsletter')
	const tFooter = useTranslations('components.footer.newsletter')

	const config = useMemo(() => {
		const colorVariant = data?.colorVariant || NewsletterColor.CYCLAMEN
		return COLOR_CONFIGS[colorVariant] || COLOR_CONFIGS[NewsletterColor.CYCLAMEN]
	}, [data?.colorVariant])

	useEffect(() => {
		setMounted(true)
	}, [])

	const {
		register,
		handleSubmit,
		formState: { errors },
		reset
	} = useForm<FormValues>()

	const mutation = useMutation({
		mutationFn: (formData: FormValues) => {
			return axios.post('/v1/web/newsletter', formData)
		},
		onSuccess: (response, variables) => {
			if (response.status === 201) {
				pushSimpleEvent('generate_lead', {
					form_name: 'newsletter',
					form_destination: 'mailing-list',
					form_location: pathname,
					value: 0,
					currency: 'EUR',
					user_data: {
						email: variables.email
					}
				})
			}
			reset()
			if (response.status === 201) {
				toast.success(tFooter('success'), {
					position: 'top-right',
					className: '!bg-medante-pink-secondary',
					autoClose: 5000,
					hideProgressBar: false,
					closeOnClick: true,
					pauseOnHover: true
				})
			}
		},
		onError: (error: any) => {
			// Check if the error is related to captcha verification
			const errorMessage = error?.response?.data?.error || error?.message || ''
			const isCaptchaError =
				errorMessage.toLowerCase().includes('captcha') ||
				errorMessage.toLowerCase().includes('recaptcha')

			const message = isCaptchaError ? tFooter('error.captcha') : tFooter('error.submit')

			toast.error(message, {
				className: '!bg-medante-pink-secondary',
				position: 'top-right',
				autoClose: 5000,
				hideProgressBar: false,
				closeOnClick: true,
				pauseOnHover: true
			})
			console.error('Error submitting newsletter form:', error)
		}
	})

	const onSubmit = async (formData: FormValues) => {
		if (!executeRecaptcha) {
			console.warn('reCAPTCHA not available')
			mutation.mutate({
				...formData,
				recaptchaToken: null
			})
			return
		}

		try {
			const token = await executeRecaptcha('newsletter_submit')
			mutation.mutate({
				...formData,
				recaptchaToken: token
			})
		} catch (error) {
			console.error('reCAPTCHA error:', error)
			mutation.mutate({
				...formData,
				recaptchaToken: null
			})
		}
	}

	if (!mounted) {
		return null
	}

	return (
		<div className='editorjs-renderer_block my-8'>
			<div
				className={`${config.color} rounded-[15px] relative overflow-hidden px-6 py-8 md:px-12 md:py-12`}
			>
				{/* Decorative vertical stripes */}
				<div className='absolute inset-0 flex justify-start items-end pointer-events-none bottom-[-1px] left-[-90px]'>
					{config.illustration && (
						<Image
							unoptimized={true}
							src={config.illustration}
							alt='Newsletter Illustration'
							width={400}
							height={197}
							className='opacity-30'
						/>
					)}
				</div>

				{/* Content */}
				<div className='relative z-10 max-w-[500px]'>
					<h3 className='title_h2 !text-white !mb-2 !w-full'>{t('title')}</h3>
					<p className='!text-white/90 !text-base !font-light !mb-5 !w-full'>
						{t('description')}
					</p>

					<form onSubmit={handleSubmit(onSubmit)} className='space-y-4'>
						{/* Email Input */}
						<div>
							<input
								type='email'
								{...register('email', {
									required: tFooter('error.email'),
									pattern: {
										value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
										message: tFooter('error.email')
									}
								})}
								placeholder={t('emailPlaceholder')}
								className='w-full px-4 py-3 md:px-5 md:py-4 bg-transparent border-2 border-white/80 focus:border-white/100 rounded-[15px] text-white placeholder-white/90 focus:outline-none focus:ring-none transition-all text-base'
								autoComplete='email'
							/>
							{errors.email && (
								<p className='text-sm text-white animate-pulse px-3 py-1 rounded font-light'>
									{errors.email.message}
								</p>
							)}
						</div>

						{/* Marketing Consent Checkbox */}
						<label className='input_checkbox_cont !mb-2 md:!mb-3'>
							<div className='checkbox-wrapper'>
								<input
									type='checkbox'
									className={`input_checkbox ${
										errors.agreedToPersonalDataProcessingForMarketingPurposes
											? 'error'
											: ''
									}`}
									{...register(
										'agreedToPersonalDataProcessingForMarketingPurposes',
										{
											required: tFooter('error.marketing')
										}
									)}
								/>
								<div className='input_checkbox_text !text-sm !font-light !text-white/90 !cursor-pointer'>
									{t('consentText')}{' '}
									<Link
										href='/legal/marketing-consent'
										className='underline !hover:text-white !text-sm !font-light !text-white/90'
										prefetch={false}
										target='_blank'
									>
										{t('consentLink')}
									</Link>
									.
								</div>
							</div>
							{errors.agreedToPersonalDataProcessingForMarketingPurposes && (
								<div className='text-sm text-white animate-pulse px-3 py-1 rounded font-light mt-2'>
									{tFooter('error.marketing')}
								</div>
							)}
						</label>

						<button
							type='submit'
							disabled={mutation.isPending}
							className={`btn_basic type2 !cursor-pointer mt-4 ${config.buttonClass}`}
						>
							<span>{t('submitButton')}</span>

							<span>
								<span>
									<svg className='sico'>
										<use href={`/assets/layout/icons.svg#${'s_ar2_r'}`} />
									</svg>
								</span>
								<span>
									<svg className='sico'>
										<use href={`/assets/layout/icons.svg#${'s_ar2_r'}`} />
									</svg>
								</span>
							</span>
						</button>
					</form>
				</div>
			</div>
		</div>
	)
}

export default BEditorJsNewsletter
